import {useEffect,useState,useMemo,useCallback,useRef} from "react";
import {useNavigate} from 'react-router-dom';
import {useSnackbar} from '../SnackbarContext.js';
import {useLoading} from '../LoadingContext';
import {consoleLog,getLimitFromParams,stripPagingParams} from '../../utilities/Utilities';
import {getObjectRecords,deleteObjectRecordsByIdList,updateObjectRecord,getObjectRecordCount} from '../../integration/ObjectManager.js';
import {useConfirm} from '../ConfirmContext.js';
import COMPONENT_MAP from '../Repository/Components.js';
import {useLocation} from 'react-router-dom';
import {useNotifications} from '../Notification/NotificationsContext.js'
let rowId=null;
let parent={};
const PageList=({userData,refreshToken=0,application,entityType,noRefresh=false,entityParams,file,list_name,focusOnSearchInput=false,navigateOnNew,filterComponent,title,checkboxSelection,disableRowSelectionOnClick,disableSelectionOnClick,selectionRequiresDeletePermission,defaultAllSelected,defaultSelectedIds,popupComponent:PopupComponent,popupComponentQuery=[],rows=[],rowsEntityRules={},pageCallback,isLoading=true,button_actions,isPopUp=false,executeQuery={current:true},height,canCreateNew=true,getRowClassName=null,dataGridSx=null,componentWhenNoRecords=null,defaultQuickSearchColumns})=>{
    const [records,setRecords]=useState(rows);
    const [entityRules,setEntityRules]=useState(rowsEntityRules);
    const [loading,setLoading]=useState(isLoading);
    const navigate=useNavigate();
    const {showSnackbar}=useSnackbar();
    const {showLoading,updateProgress,hideLoading}=useLoading();
    //defaultAllSelected/defaultSelectedIds are only meaningful for a static rows=[...] popup
    //(isPopUp with executeQuery:{current:false}) - they seed the initial selection up front
    //instead of the usual empty-until-the-user-clicks-something default. defaultAllSelected seeds
    //every row ("proceed with changes on selected, all selected by default"); defaultSelectedIds
    //seeds only the given subset (e.g. "here's every eligible row, but only the ones that actually
    //diverge start checked"). Makes the DataGrid's row selection controlled (rowSelectionModel
    //below) in either case - every other caller keeps the DataGrid managing its own uncontrolled
    //selection state exactly as before.
    const [selection,setSelection]=useState(()=>{
        if(!checkboxSelection){return [];}
        if(defaultSelectedIds){return defaultSelectedIds;}
        return defaultAllSelected?rows.map(row=>row.id):[];
    });
    const [openPopup,setOpenPopup]=useState(false);
    const {confirm}=useConfirm();
    const location=useLocation();
    const [showComponentWhenNoRecords,setShowComponentWhenNoRecords]=useState(false);
    const entityParamsKey=useMemo(()=>(entityParams??[]).join("|"),[entityParams]);
    const runningRef=useRef(false);
    const {refreshNotifications}=useNotifications();

    const getRecords=useCallback(async(isSubscribed=true)=>{
        if(runningRef.current) return;
        runningRef.current=true;

        if (!isSubscribed) return;

        try{
            const limit=getLimitFromParams(entityParams);
            const isBatchMode=!!limit;

            if(!isBatchMode){
                const data=await getObjectRecords({entityType,entityParams});
                if (!isSubscribed) return;
                consoleLog({file:file,context:'getRecords - getObjectRecords',arg_name:'data',arg_value:data});

                if('error' in data){
                    showSnackbar(data.error,'error');
                    setRecords([]);
                } 
                else{
                    if(componentWhenNoRecords&&data.records.length===0){
                        setShowComponentWhenNoRecords(true);
                    }
                    else{
                        setRecords(data.records||[]);
                        setEntityRules(data.entity_rules);
                    }
                }

                setLoading(false);
                executeQuery.current=false;
                return;
            }

            showLoading(0);
            const baseParams=stripPagingParams(entityParams);
            const countData=await getObjectRecordCount({entityType,entityParams:baseParams});
            consoleLog({file:file,context:'getRecords - getObjectRecordCount',arg_name:'countData',arg_value:countData});
            if('error' in countData){
                showSnackbar(countData.error,'error');
                hideLoading();
                setLoading(false);
                return;
            }

            const total=countData.count||0;
            let offset=0;
            setRecords([]);
            let entityRulesLoaded=false;
            if(total===0){
                const data=await getObjectRecords({entityType,entityParams:baseParams});
                consoleLog({file:file,context:'getRecords - getObjectRecords',arg_name:'data',arg_value:data});
                if('error' in data){
                    showSnackbar(data.error,'error');
                }
                if(!entityRulesLoaded&&data.entity_rules){
                    setEntityRules(data.entity_rules);
                    entityRulesLoaded=true;
                }
            }
            else {
                while(offset<total){
                    const batchParams=[...baseParams,`_limit=${limit}`,`_offset=${offset}`,];
                    const data=await getObjectRecords({entityType,entityParams:batchParams,});
                    if (!isSubscribed) return;
                    consoleLog({file:file,context:'getRecords - getObjectRecords',arg_name:'data',arg_value:data});
                    if('error' in data){
                        showSnackbar(data.error,'error');
                        break;
                    }
                    setRecords(prev=>[...prev,...(data.records||[])]);
                    offset+=data.records.length;
                    const percent=Math.min(100,Math.round((offset/total)*100));
                    updateProgress(percent);
                    if(!entityRulesLoaded&&data.entity_rules){
                        setEntityRules(data.entity_rules);
                        entityRulesLoaded=true;
                    }
                    await new Promise(resolve=>setTimeout(resolve,80));
                }
            }
            executeQuery.current=false;
        }
        finally{
            runningRef.current=false;
            hideLoading();
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[entityType,entityParamsKey]);

    useEffect(()=>{
        let isSubscribed=true;
        if(executeQuery.current){
            getRecords(isSubscribed);
        }
        return()=>{
            isSubscribed=false;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[entityParamsKey,getRecords,refreshToken]);

    const updateRecordField=async(entityId,entityData)=>{
            let data=await updateObjectRecord({
                entityType:entityType,
                entityId:entityId,
                entityData:entityData,
                getUpdatedRecord:false
            });
            //A backend business validation rejection comes back as header_validation, not error -
            //normalize it so the snackbar shows it and List.processRowUpdate reverts the edited cell
            if(data?.header_validation?.error){
                data={error:data.header_validation.message};
            }
            if('error' in data){
                showSnackbar(data.error, 'error');
            } 
            else if(data?.operation_message){//saved, but the backend adjusted something the user must know (e.g. a rounded value)
                showSnackbar(data.operation_message,'warning');
            }
            else{
                showSnackbar(application.getLocale("RecordUpdatedWithSuccess"),'success');
            }
            return data;
        };

    const handleClickEvent=async(args)=>{
        consoleLog({file:file,context:'handleClickEvent',arg_name:'args',arg_value:args});
        switch(args?.action){
            case "updateFields":
                return updateRecordField(args.entityId,args.entityData);
            case "new":
                if(navigateOnNew){
                    navigate(navigateOnNew,{state:{from:location.pathname}});//open on a new page
                }
                else{
                    rowId=null;
                    setOpenPopup(true);//open a popup
                }
                break;
            case "refresh":
                setRecords([]);
                executeQuery.current = true;
                getRecords();
                break;
            case "selectRow":
                pageCallback('selectRow',{id:args?.id,pickMap:args?.pickMap});
                break;
            case "openPopup":
                rowId=args?.id;
                setOpenPopup(true);
                break;
            case "removeSelection":
                
                if(await confirm({message:selection.length>1?application.getLocale("ConfirmMultipleRecordDeletion").replace("{RECORDS}",selection.length):application.getLocale("ConfirmRecordDeletion")})){
                    deleteRecords(selection);
                }
                break;
            case "close_popup":
                pageCallback('close_popup',{});
                break;
            case "proceed_update_tasks":
                pageCallback('proceed_update_tasks',{});
                break;
            case "proceed_no_changes":
                pageCallback('proceed_no_changes',{});
                break;
            case "invoke_method":
                pageCallback('invoke_method',args);
                break;
            default:
                //Generic passthrough for any button_actions name not handled above (e.g. a
                //custom action a popup defines for itself), including the current checkbox
                //selection - without this, an unrecognized action name silently did nothing.
                if(args?.action){
                    pageCallback(args.action,{...(args?.args||{}),selection});
                }
                break;
        }
    }

    const handleSelection=(event)=>{
        const ids=Array.from(event.ids);
        consoleLog({file:file,context:'onRowSelectionModelChange',arg_name:'ids',arg_value:ids});
        setSelection(old=>ids);
    };

    //Same (action,args) contract as ListTab.js's popupCallBack - popups that forward every
    //PageDetail pageCallback action (e.g. 'recordLoaded', fired as soon as the record loads)
    //were previously read as a truthy "refresh" flag, closing the popup right after it opened.
    //A plain boolean is still accepted for popups using the legacy form (e.g. ErrorLogDetailPopup).
    const popupCallBack=(action,args)=>{
        if(typeof action!=='string'){
            setOpenPopup(false);
            if(action){
                handleClickEvent({action:'refresh'});
            }
        }
        else if(action==='refreshParent'){
            handleClickEvent({action:'refresh'});
            if(args?.close){
                setOpenPopup(false);
            }
        }
        else if(action==='closePopup'){
            setOpenPopup(false);
        }
        else if(action==='invokeMethod'){
            setOpenPopup(false);
            pageCallback?.('invoke_method',args);
        }
    }

    const deleteRecords=(idList)=>{
        consoleLog({file:file,context:'deleteRecord',arg_name:'idList',arg_value:idList});
        showLoading();
        //Forward the list's own _context (e.g. parent_name/parent_id) so models whose
        //can_delete_record rules depend on the parent record can resolve it - without this,
        //every context-dependent model rejects the delete regardless of permissions.
        const contextParam=(entityParams??[]).find((param)=>param.startsWith('_context='));
        const deleteParams=contextParam?['idList='+idList,contextParam]:['idList='+idList];
        deleteObjectRecordsByIdList({entityType:entityType,entityParams:deleteParams})
        .then((data)=>{
            consoleLog({file:file,context:'deleteRecords - deleteObjectRecordsByIdList',arg_name:'data',arg_value:data});
            if('error' in data){
                showSnackbar(data.error,'error');
            }
            else{
                showSnackbar(data.deleted>1?application.getLocale("RecordsRemovedSuccess"):application.getLocale("RecordRemovedSuccess"),'success');//update string
                setSelection(old=>[]);
                getRecords();
                if(entityType==='Notifications'){
                    refreshNotifications();
                }
            }
            hideLoading();
        });
    };

    const renderPageHeader=()=>{
        const Component=COMPONENT_MAP['PageHeader'];
        return Component?(
            <Component
                title={title}
            />
        ):null; 
    };

    const renderList=useMemo(()=>{
        const Component=COMPONENT_MAP['List'];
        return Component?(
            <Component
                records={records}
                list_name={list_name}
                loading={loading}
                height={height}
                file={file}
                application={application}
                initialState={null}
                canCreateNew={canCreateNew}
                entityRules={entityRules}
                userData={userData}
                noRefresh={noRefresh}
                getRowClassName={getRowClassName}
                dataGridSx={dataGridSx}
                focusOnSearchInput={focusOnSearchInput}
                button_actions={button_actions}
                handleClickEvent={handleClickEvent}
                handleSelection={handleSelection}
                filterComponent={filterComponent}
                checkboxSelection={checkboxSelection??false}
                disableRowSelectionOnClick={disableRowSelectionOnClick??false}
                disableSelectionOnClick={disableSelectionOnClick??false}
                selectionRequiresDeletePermission={selectionRequiresDeletePermission}
                rowSelectionModel={(defaultAllSelected||defaultSelectedIds)?{type:'include',ids:new Set(selection)}:undefined}
                canRemoveSelection={selection.length>0&&(selectionRequiresDeletePermission??true)}
                isPopUp={isPopUp}
                defaultQuickSearchColumns={defaultQuickSearchColumns}
            />
        ):null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[loading,records,entityRules,selection,application.globalData.dataGridParams[list_name]])

    const renderDetailFormPopup=()=>{
        const Component=COMPONENT_MAP['DetailFormPopup'];
        return Component?(
            <Component
                key={'DetailFormPopup'}
                title={title}
                application={application}
                handleClickEvent={handleClickEvent}
                isChanged={false}
                componentBody={renderList}
                readOnly={true}
                recordRules={null}
            />
        ):null; 
    };

    return (
        <>
        {!isPopUp&&title&&
        <div className="page-list">
            {openPopup&&<PopupComponent popupCallBack={popupCallBack} query={popupComponentQuery} application={application} rowId={rowId} parent={parent} userData={userData}></PopupComponent>}
            {renderPageHeader()}
            {renderList}
        </div>}
        {!title&&renderList()}
        {isPopUp&&!showComponentWhenNoRecords&&renderDetailFormPopup()}
        {isPopUp&&showComponentWhenNoRecords&&componentWhenNoRecords}
        </>
        
    );
}

export default PageList;